import { useEffect, useRef, useState } from 'react'
import type { OKFContext, OKFNote, OKFRecord } from '@aide/platform-types'
import api from './services/api'
import { useAuth } from './hooks/useAuth'

const button = 'rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50'
const field = 'w-full rounded-md border border-slate-300 bg-white p-2 text-sm'

export default function SourceContextPanel({ repoId }: { repoId: string }) {
  const { profile } = useAuth()
  return <ProjectPicker key={`${repoId}:${profile?.tenant_id}:${profile?.id}`} />
}

export function ProjectPicker() {
  const [projects, setProjects] = useState<Array<{ project_id: string; name: string }>>([])
  const [project, setProject] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let active = true
    api.listContextProjects().then(value => { if (active) setProjects(value.projects) })
      .catch(error => { if (active) setError(error.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])
  return <section className="space-y-5 py-6" aria-label="My source context">
    <h2 className="text-xl font-semibold">My source context</h2>
    <p className="text-sm text-slate-600">Source knowledge and your notes, alongside the code. Context comes from the selected Contextful project and remains private to you.</p>
    {error && <p role="alert">{error}</p>}
    {loading ? <p role="status">Loading projects…</p> : <>
      <label className="block text-sm">Contextful project<select className={`${field} mt-1`} value={project} onChange={event => setProject(event.target.value)}>
        <option value="">Choose a project</option>{projects.map(item => <option key={item.project_id} value={item.project_id}>{item.name}</option>)}
      </select></label>
      {!projects.length && !error && <p>No Contextful projects available. Create a project in Contextful to publish source knowledge.</p>}
      {projects.length === 500 && <p className="text-sm">Showing the first 500 projects. Use Contextful to find projects outside this list.</p>}
    </>}
    {project && <ProjectSourceContext key={project} projectId={project} />}
  </section>
}

export function ProjectSourceContext({ projectId }: { projectId: string }) {
  const [sources, setSources] = useState<OKFRecord[]>([])
  const [notes, setNotes] = useState<OKFRecord[]>([])
  const [more, setMore] = useState({ source: false, note: false })
  const [revisions, setRevisions] = useState<string[]>([])
  const [noteIds, setNoteIds] = useState<string[]>([])
  const [inspected, setInspected] = useState<(OKFNote & { author_id: string }) | null>(null)
  const [task, setTask] = useState('')
  const [contextSize, setContextSize] = useState(24000)
  const [context, setContext] = useState<OKFContext | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(true)
  const alive = useRef(true)

  async function load() {
    const [source, note] = await Promise.all([api.listSourceKnowledge(projectId, 'source'), api.listSourceKnowledge(projectId, 'note')])
    if (!alive.current) return
    setSources(source.records); setNotes(note.records)
    setMore({ source: source.records.length === 100, note: note.records.length === 100 })
  }
  async function run(action: () => Promise<void>) {
    setBusy(true); setError(''); setContext(null)
    try { await action() } catch (error) { if (alive.current) setError(error instanceof Error ? error.message : 'Request failed') }
    finally { if (alive.current) setBusy(false) }
  }
  useEffect(() => {
    alive.current = true
    void run(load)
    return () => { alive.current = false }
    // The parent keys this component by project; late responses are discarded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function toggle(id: string, kind: 'source' | 'note') {
    setContext(null)
    const setter = kind === 'source' ? setRevisions : setNoteIds
    setter(items => items.includes(id) ? items.filter(item => item !== id) : [...items, id])
  }
  async function moreRecords(kind: 'source' | 'note') {
    const rows = await api.listSourceKnowledge(projectId, kind, (kind === 'source' ? sources : notes).length)
    if (!alive.current) return
    const setter = kind === 'source' ? setSources : setNotes
    setter(items => [...items, ...rows.records]); setMore(value => ({ ...value, [kind]: rows.records.length === 100 }))
  }
  function download() {
    const url = URL.createObjectURL(new Blob([JSON.stringify(context, null, 2)], { type: 'application/json' }))
    const link = document.createElement('a'); link.href = url; link.download = 'forge-source-context.json'; link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return <div className="space-y-5">
    <div className="flex flex-wrap gap-3">
      <a className={button} href={`https://contextful.int10h.de/p/${encodeURIComponent(projectId)}/knowledge/sources`} target="_blank" rel="noreferrer">Manage in Contextful</a>
      <button className={button} disabled={busy} onClick={() => void run(async () => { setRevisions([]); setNoteIds([]); setInspected(null); await load() })}>Refresh publications</button>
    </div>
    <p className="text-sm text-slate-600">Only publications owned by your signed-in identity are available. Preparing context does not call a model, consume an AI request, or attach private knowledge to a shared pull request.</p>
    {error && <p role="alert" className="rounded bg-red-50 p-3 text-red-800">{error}</p>}
    {busy && <p role="status">Loading source context…</p>}
    {(['source', 'note'] as const).map(kind => {
      const rows = kind === 'source' ? sources : notes
      const selected = kind === 'source' ? revisions : noteIds
      return <section key={kind} className="space-y-3 rounded-md border border-slate-200 p-4" aria-label={kind === 'source' ? 'Source revisions' : 'Personal guidance'}>
        <h3 className="font-semibold">{kind === 'source' ? 'Source revisions' : 'Personal guidance'}</h3>
        {!rows.length && !busy && !error && <p className="text-sm">{kind === 'source' ? 'No source publications in this project.' : 'No personal guidance in this project.'}</p>}
        {rows.map(row => <div key={row.artifact_id} className="flex flex-wrap items-center justify-between gap-2">
          <label className="flex min-w-0 gap-2 text-sm"><input type="checkbox" checked={selected.includes(row.artifact_id)} disabled={busy || row.status !== 'ready' || (!selected.includes(row.artifact_id) && selected.length >= (kind === 'source' ? 8 : 20))} onChange={() => toggle(row.artifact_id, kind)} />
            <span className="min-w-0 break-words">{row.metadata.bundle_id || row.metadata.kind}<span className="block text-xs text-slate-500">{new Date(row.created_at).toLocaleString()} · {row.status === 'ready' ? (kind === 'source' ? 'Unverified source' : 'Personal testimony') : 'Withdrawn'}<span className="block break-all">{row.artifact_id}{row.metadata.revision_hash ? ` · ${row.metadata.revision_hash.slice(0, 12)}` : ''}</span></span></span>
          </label>
          {kind === 'note' && row.status === 'ready' && <button className={button} disabled={busy} onClick={() => void run(async () => { const note = await api.readSourceNote(projectId, row.artifact_id); if (alive.current) setInspected(note) })}>Inspect note</button>}
        </div>)}
        {more[kind] && <button className={button} disabled={busy} onClick={() => void run(() => moreRecords(kind))}>Load older {kind === 'source' ? 'revisions' : 'notes'}</button>}
      </section>
    })}
    {inspected && <article className="rounded-md bg-slate-50 p-4 text-sm"><p>Personal testimony by {inspected.author_id}</p><p className="my-2 whitespace-pre-wrap">{inspected.content}</p><p>Applies when: {inspected.applicability}</p><ul className="mt-2 space-y-1 break-all text-xs">{inspected.targets.map((target, i) => <li key={i}>{target.concept_id} · {target.revision_id}</li>)}</ul></article>}
    <label className="block text-sm">Repository task<textarea className={`${field} mt-1`} maxLength={2000} disabled={busy} value={task} onChange={event => { setTask(event.target.value); setContext(null) }} /></label>
    <label className="block text-sm">Context size<select aria-label="Context size" className={`${field} mt-1`} disabled={busy} value={contextSize} onChange={event => { setContextSize(Number(event.target.value)); setContext(null) }}><option value={24000}>Standard</option><option value={64000}>Extended — more sources and notes</option></select></label>
    <button className={button} disabled={busy || !revisions.length || !task.trim()} onClick={() => void run(async () => {
      const result = await api.prepareSourceContext(projectId, { task, revision_ids: revisions, note_ids: noteIds, ...(contextSize === 24000 ? {} : { max_characters: contextSize }) })
      if (alive.current) setContext(result)
    })}>Prepare source context</button>
    {context && <section aria-label="Prepared context" className="space-y-4 rounded-md border border-slate-200 p-4">
      <h3 className="font-semibold">Prepared context</h3>
      <p>{context.source_knowledge.length} source concepts · {context.expert_guidance.length} personal notes · {context.omissions.length} omitted items</p>
      {context.omissions.some(item => item.reason === 'context-budget') && <p role="status" className="rounded bg-amber-50 p-3 text-sm text-amber-900">Some selected knowledge did not fit. {contextSize === 24000 ? 'Choose extended context or fewer source revisions' : 'Choose fewer source revisions'}, then prepare again. Notes also need their supporting concepts included.</p>}
      <h4 className="font-medium">Source knowledge</h4>
      {context.source_knowledge.map((source, i) => <details key={i}><summary>{source.title} · Unverified source</summary><p className="my-2 whitespace-pre-wrap">{source.content}</p><p className="break-all text-xs">{source.revision_id} · {source.concept_id}</p></details>)}
      <h4 className="font-medium">Expert guidance</h4>
      {context.expert_guidance.map(note => <article key={note.note_id} className="rounded bg-slate-50 p-3"><p className="text-xs">Personal testimony by {note.author_id}</p><p className="my-2 whitespace-pre-wrap">{note.content}</p><p className="text-sm">Applies when: {note.applicability}</p></article>)}
      <p className="text-sm">{context.instructions}</p>
      <button className={button} onClick={download}>Download source context</button>
      <details><summary>Provenance and omitted items</summary><pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs">{JSON.stringify(context, null, 2)}</pre></details>
    </section>}
  </div>
}
